<script setup lang="ts">
const { t: $t } = useI18n()

import type { SignedUVV } from './UVVCard.vue'

const props = defineProps<{ payload: SignedUVV }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { machineryUVVReport } = useQuery()
const { data: uvvCustomerSignature, isLoading } = machineryUVVReport.getCustomerSignatureById(props.payload.id)

const showSignatureFields = ref(false)

const formData = computed(() => ({
  id: props.payload.id,
  signedUvv: props.payload.signedUvv,
  customerName: props.payload.customerName,
  customerSignature: uvvCustomerSignature.value ? uvvCustomerSignature.value.customerSignature : undefined,
}))

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()
const uploadSignedUVV = useMutation({
  mutationFn: $trpc.machineryUVVReport.uploadSignedUVV.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('machinery.notification.uploadSignedUvv.error') }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['machineryUVVReport'] })
    notification.success({ title: $t('machinery.notification.uploadSignedUvv.success'), duration: 4500 })
    emit('close')
  },
})
</script>

<template>
  <ThePopup show :title="$t('machinery.detailsPage.uvv.signedUvv.popUp.title')" @close="emit('close')">
    <FormKit
      v-slot="{ value }: { value: any }"
      type="form"
      :disabled="isLoading"
      @submit="(v) => uploadSignedUVV.mutate(v)"
    >
      <FormKit name="id" :value="formData.id" type="hidden" />
      <FormKitFileDropzone
        v-if="!showSignatureFields"
        name="signedUvv"
        :value="formData.signedUvv ? [{ path: formData.signedUvv }] : []"
        validation="required|max:1"
      />
      <div class="flex flex-col gap-2 pb-4">
        <n-checkbox v-model:checked="showSignatureFields" :disabled="value.signedUvv?.length > 0">
          {{ $t('machinery.detailsPage.uvv.signedUvv.popUp.signDirectly.checkbox.label') }}
        </n-checkbox>
        <div v-if="showSignatureFields && (!value.signedUvv || value.signedUvv.length === 0)" class="flex gap-5">
          <FormKit
            name="customerName"
            :value="formData.customerName"
            :label="$t('machinery.detailsPage.uvv.signedUvv.popUp.customerName.label')"
            type="text"
          />
          <FormKitSignaturePopup
            name="customerSignature"
            :value="formData.customerSignature"
            :label="$t('machinery.detailsPage.uvv.signedUvv.popUp.customerSignature.label')"
            :title="$t('machinery.detailsPage.uvv.signedUvv.popUp.customerSignature.signDirectly.title')"
            :disable-by-pass="true"
            :disable-positions-overview="true"
          />
        </div>
      </div>
    </FormKit>
  </ThePopup>
</template>
